  import React, { useState, useEffect } from 'react';
  import { fetchCoaches, createCoach, updateCoach, deleteCoach } from '../../../services/coachService';
  import {fetchClubs} from '../../../services/clubService';
  import uploadToDropbox from '../../../services/dropboxService';
  import { SearchInput } from '../../admin';

  function CoachesTab() {
    const [coaches, setCoaches] = useState([]);
    const [clubs, setClubs] = useState([]);
    const [selectedClub, setSelectedClub] = useState(null);
    const [filteredCoaches, setFilteredCoaches] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [currentCoach, setCurrentCoach] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [uploading, setUploading] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);

    useEffect(() => {
      loadClubs();
      loadCoaches();
    }, []);

    const loadClubs = async () => {
      try {
        const response = await fetchClubs();
        if (response) {
          setClubs(response);
        } else {
          setError('Failed to load clubs');
        }
      } catch (err) {
        setError(err.message);
      }
    };

    const loadCoaches = async () => {
      try {
        setLoading(true);
        const response = await fetchCoaches();
        if (response.success) {
          setCoaches(response.data);
          setFilteredCoaches(response.data);
        } else {
          setError(response.message || 'Failed to load coaches');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    const handleSearch = (query) => {
      setSearchQuery(query);
      if (!query.trim()) {
        setFilteredCoaches(coaches);
        return;
      }

      const searchTerm = query.toLowerCase();
      const filtered = coaches.filter(coach => 
        coach.firstName.toLowerCase().includes(searchTerm) ||
        coach.lastName.toLowerCase().includes(searchTerm) ||
        coach.email.toLowerCase().includes(searchTerm) ||
        (coach.phoneNumber && coach.phoneNumber.toLowerCase().includes(searchTerm)) ||
        (coach.nationality && coach.nationality.toLowerCase().includes(searchTerm))
      );
      setFilteredCoaches(filtered);
    };

    const handleAddCoach = async (coachData) => {
      try {
        setLoading(true);
        const response = await createCoach(coachData);
        if (response.success) {
          const newCoaches = [...coaches, response.data];
          setCoaches(newCoaches);
          setFilteredCoaches(newCoaches);
          setShowModal(false);
        } else {
          setError(response.message || 'Failed to add coach');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    const handleEditCoach = async (coachData) => {
      try {
        setLoading(true);
        const response = await updateCoach(coachData.id, coachData);
        if (response.success) {
          const updatedCoaches = coaches.map(coach => 
            coach.id === response.data.id ? response.data : coach
          );
          setCoaches(updatedCoaches);
          setFilteredCoaches(updatedCoaches);
          setShowModal(false);
          setCurrentCoach(null);
        } else {
          setError(response.message || 'Failed to update coach');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    const handleDeleteCoach = async (id) => {
      try {
        setLoading(true);
        const response = await deleteCoach(id);
        if (response && response.success) {
          const newCoaches = coaches.filter(coach => coach.id !== id);
          setCoaches(newCoaches);
          setFilteredCoaches(newCoaches);
        } else {
          setError(response?.message || 'Failed to delete coach');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    const handleEdit = (coach) => {
      setCurrentCoach(coach);
      // Set selectedClub based on coach's club
      const club = clubs.find(c => c.name === coach.Club?.name);
      setSelectedClub(club || null);
      setShowModal(true);
    };

    const handleFileUpload = async (file) => {
      try {
        setUploading(true);
        const url = await uploadToDropbox(file);
        setPreviewUrl(url);
        return url;
      } catch (err) {
        setError(err.message);
        return null;
      } finally {
        setUploading(false);
      }
    };

    const handleImageChange = async (e) => {
      const file = e.target.files[0];
      if (file) {
        const url = await handleFileUpload(file);
        if (url) {
          // Update the form's profilePicture field
          const form = e.target.closest('form');
          if (form) {
            const profilePictureInput = form.querySelector('input[name="profilePicture"]');
            if (profilePictureInput) {
              profilePictureInput.value = url;
            }
          }
        }
      }
    };

    if (loading) return <div className="text-white text-center p-4">Loading...</div>;
    if (error) return <div className="text-accent-soft text-center p-4">Error: {error}</div>;

    return (
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <SearchInput
            value={searchQuery}
            onChange={handleSearch}
            placeholder="Search coaches by name, email or phone…"
          />
          <button
            type="button"
            onClick={() => {
              setCurrentCoach(null);
              setSelectedClub(null);
              setShowModal(true);
            }}
            className="btn-primary shrink-0 sm:ml-auto"
          >
            Add Coach
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCoaches.map((coach) => (
            <div key={coach.id} className="bg-surface-800 border border-line rounded-lg p-6 hover:border-line-strong transition-colors duration-200">
              <h3 className="text-xl font-semibold text-white mb-2">{coach.firstName} {coach.lastName}</h3>
              <div className="space-y-2 text-gray-300">
                <p className="flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Email: {coach.email}
                </p>
                <p className="flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  Phone: {coach.phoneNumber || 'N/A'}
                </p>
                <p className="flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                  </svg>
                  Nationality: {coach.nationality || 'N/A'}
                </p>
                <p className="flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Status: {coach.status}
                </p>
                <p className="flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Current Club: {coach.Club ? coach.Club.name : 'Unassigned'}
                </p>
              </div>
              <div className="mt-4 flex gap-2">
                <button
                  onClick={() => handleEdit(coach)}
                  className="flex-1 bg-surface-600 hover:bg-surface-700 text-white px-3 py-2 rounded-lg transition-colors duration-200"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDeleteCoach(coach.id)}
                  className="flex-1 bg-accent hover:bg-accent-hover text-white px-3 py-2 rounded-lg transition-colors duration-200"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>

        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
            <div className="bg-surface-800 p-8 rounded-lg w-[600px] max-h-[90vh] overflow-y-auto border border-line">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">
                  {currentCoach ? 'Edit Coach' : 'Add Coach'}
                </h2>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setCurrentCoach(null);
                    setSelectedClub(null);
                    setPreviewUrl(null);
                  }}
                  className="text-gray-400 hover:text-white"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target);
                  const coachData = {
                    firstName: formData.get('firstName'),
                    lastName: formData.get('lastName'),
                    email: formData.get('email'),
                    phoneNumber: formData.get('phoneNumber'),
                    dateOfBirth: formData.get('dateOfBirth'),
                    nationality: formData.get('nationality'),
                    profilePicture: formData.get('profilePicture'),
                    status: formData.get('status'),
                    clubId: selectedClub?.clubId || null,
                  };
                  if (currentCoach) {
                    handleEditCoach({ ...coachData, id: currentCoach.id });
                  } else {
                    handleAddCoach(coachData);
                  }
                }}
                className="space-y-6"
              >
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">First Name</label>
                      <input
                        type="text"
                        name="firstName"
                        defaultValue={currentCoach?.firstName}
                        className="w-full px-4 py-2 bg-surface-700 text-white border border-line-strong rounded-lg focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Last Name</label>
                      <input
                        type="text"
                        name="lastName"
                        defaultValue={currentCoach?.lastName}
                        className="w-full px-4 py-2 bg-surface-700 text-white border border-line-strong rounded-lg focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Email</label>
                      <input
                        type="email"
                        name="email"
                        defaultValue={currentCoach?.email}
                        className="w-full px-4 py-2 bg-surface-700 text-white border border-line-strong rounded-lg focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Phone Number</label>
                      <input
                        type="tel"
                        name="phoneNumber"
                        defaultValue={currentCoach?.phoneNumber}
                        className="w-full px-4 py-2 bg-surface-700 text-white border border-line-strong rounded-lg focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent"
                      />
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Date of Birth</label>
                      <input
                        type="date"
                        name="dateOfBirth"
                        defaultValue={currentCoach?.dateOfBirth?.split('T')[0]}
                        className="w-full px-4 py-2 bg-surface-700 text-white border border-line-strong rounded-lg focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Nationality</label>
                      <input
                        type="text"
                        name="nationality"
                        defaultValue={currentCoach?.nationality}
                        className="w-full px-4 py-2 bg-surface-700 text-white border border-line-strong rounded-lg focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Status</label>
                      <select
                        name="status"
                        defaultValue={currentCoach?.status || 'active'}
                        className="w-full px-4 py-2 bg-surface-700 text-white border border-line-strong rounded-lg focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent"
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Current Club</label>
                      <select
                        name="clubId"
                        value={selectedClub?.name || ''}
                        onChange={(e) => {
                          const club = clubs.find(c => c.name === e.target.value);
                          setSelectedClub(club || null);
                        }}
                        className="w-full px-4 py-2 bg-surface-700 text-white border border-line-strong rounded-lg focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent"
                      >
                        <option value="">Unassigned</option>
                        {clubs.map((club) => (
                          <option key={club.clubId} value={club.name}>
                            {club.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Profile Picture</label>
                      <div className="flex items-center space-x-4">
                        <div className="flex-1">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageChange}
                            className="hidden"
                            id="profilePictureInput"
                          />
                          <label
                            htmlFor="profilePictureInput"
                            className="block w-full px-4 py-2 bg-surface-700 text-white border border-line-strong rounded-lg cursor-pointer hover:bg-surface-700 transition-colors duration-200 text-center"
                          >
                            {uploading ? 'Uploading...' : 'Choose Image'}
                          </label>
                        </div>
                        <input
                          type="hidden"
                          name="profilePicture"
                          defaultValue={currentCoach?.profilePicture || previewUrl}
                        />
                      </div>
                      {(currentCoach?.profilePicture || previewUrl) && (
                        <div className="mt-2">
                          <img
                            src={previewUrl || currentCoach?.profilePicture}
                            alt="Profile preview"
                            className="w-20 h-20 object-cover rounded-lg"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4 border-t border-line">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setCurrentCoach(null);
                      setSelectedClub(null);
                      setPreviewUrl(null);
                    }}
                    className="px-6 py-2 text-gray-300 border border-line-strong rounded-lg hover:bg-surface-700 transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bg-accent hover:bg-accent-hover text-white px-6 py-2 rounded-lg transition-colors duration-200"
                  >
                    {currentCoach ? 'Update' : 'Add'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    );
  }

  export default CoachesTab; 